/**
 * Privacy-safe runtime context for an error-boundary report.
 *
 * Deliberately a fixed set of low-cardinality categories. Nothing here can
 * carry a token, a cookie, an email, an id or any other personal data: each
 * value is either a boolean or one of a handful of literal strings, and the
 * server re-validates every one against the same allow-list before recording
 * it.
 *
 * This exists because an installed-PWA error screen was reported twice with no
 * way to tell afterwards whether it was a navigation, a chunk, an RSC payload
 * or an expired session. Every read below is wrapped: diagnostics must never
 * be able to throw inside an error boundary and turn a handled error into an
 * unhandled one.
 */

export type ErrorBoundaryContext = {
  standalone: boolean;
  online: boolean;
  serviceWorker: "controlled" | "registered_uncontrolled" | "unsupported" | "unknown";
  visibility: "visible" | "hidden" | "unknown";
  navigation: "navigate" | "reload" | "back_forward" | "prerender" | "unknown";
  appVersion: string | null;
};

export const ERROR_BOUNDARY_CONTEXT_KEYS: Array<keyof ErrorBoundaryContext> = [
  "standalone",
  "online",
  "serviceWorker",
  "visibility",
  "navigation",
  "appVersion",
];

export const SERVICE_WORKER_STATES = [
  "controlled",
  "registered_uncontrolled",
  "unsupported",
  "unknown",
] as const;

export const VISIBILITY_STATES = ["visible", "hidden", "unknown"] as const;

export const NAVIGATION_TYPES = [
  "navigate",
  "reload",
  "back_forward",
  "prerender",
  "unknown",
] as const;

function safely<T>(read: () => T, fallback: T): T {
  try {
    const value = read();
    return value === undefined || value === null ? fallback : value;
  } catch {
    return fallback;
  }
}

function readStandalone(): boolean {
  return safely(() => {
    if (window.matchMedia?.("(display-mode: standalone)")?.matches) return true;
    // iOS Safari exposes the legacy flag instead of the media query.
    return (window.navigator as any)?.standalone === true;
  }, false);
}

function readServiceWorker(): ErrorBoundaryContext["serviceWorker"] {
  return safely<ErrorBoundaryContext["serviceWorker"]>(() => {
    if (!("serviceWorker" in navigator)) return "unsupported";
    return navigator.serviceWorker.controller ? "controlled" : "registered_uncontrolled";
  }, "unknown");
}

function readNavigation(): ErrorBoundaryContext["navigation"] {
  return safely<ErrorBoundaryContext["navigation"]>(() => {
    const entry = performance.getEntriesByType?.("navigation")?.[0] as
      | PerformanceNavigationTiming
      | undefined;
    const type = entry?.type;
    if (type === "navigate") return "navigate";
    if (type === "reload") return "reload";
    if (type === "back_forward") return "back_forward";
    if (type === "prerender") return "prerender";
    return "unknown";
  }, "unknown");
}

export function collectErrorBoundaryContext(): ErrorBoundaryContext | null {
  if (typeof window === "undefined") return null;
  return {
    standalone: readStandalone(),
    online: safely(() => navigator.onLine !== false, true),
    serviceWorker: readServiceWorker(),
    visibility: safely<ErrorBoundaryContext["visibility"]>(
      () => (document.visibilityState === "hidden" ? "hidden" : "visible"),
      "unknown"
    ),
    navigation: readNavigation(),
    // Build identifier only. Set at build time and already public.
    appVersion: safely(
      () => String(process.env.NEXT_PUBLIC_APP_VERSION || "").trim().slice(0, 80) || null,
      null
    ),
  };
}
